import React, { useEffect } from "react"
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Stack } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import ReplayIcon from "@mui/icons-material/Replay"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import dayjs from "dayjs"
import { getQHSEConformityPSEsSelector, getQHSEConformityReloadedPSEsSelector } from "../../../reducers/Alerts/alerts"
import { COLORS } from "../../../utils"
import { addReloadedQHSEConformityAlerts, getQHSEConformityAlertsToReload } from "../../../actions/ProductionSteps/alerts"
import { showProductionStepExecution } from "../../../actions/ProductionSteps/ProductionStepExecutions"

const sx = {
    col: {
        px: 6
    },
    text: {
        fontSize: 16,
        lineHeight: 1.5,
    },
    recipe: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.5,
    },
    section: {
        fontSize: 16,
        lineHeight: 1.5,
    },
    reloadIcon: {
        transform: "scaleX(-1) rotate(-40deg)",
    },
    reloadButton: {
        fontSize: 16,
        textTransform: "none",
        color: COLORS.LABEL_GREY,
        fontWeight: 400
    }
}

const QHSEConformity = ({ dateStr }) => {
    const dispatch = useDispatch()

    const productionStepExecutions = useSelector(getQHSEConformityPSEsSelector)
    const reloadedProductionStepExecutions = useSelector(getQHSEConformityReloadedPSEsSelector)

    /**
     * Pool function to reload the data every 5 minutes
     * @NOTE: The data is stored in the Redux store, so we can access it from any component
     */
    useEffect(() => {
        const interval = setInterval(() => {
            // This will fetch the data from the server and update the state
            dispatch(getQHSEConformityAlertsToReload(dateStr))
        }, 5 * 60 * 1000) // 5 minutes
        return () => clearInterval(interval) // Cleanup the interval on component unmount
    }, [dispatch, reloadedProductionStepExecutions.length, dateStr])

    /**
     * Update the data table when the user clicks on the reload button
     * @NOTE: It's already in the Redux store (in the pool useEffect), so we just need to update the state
     * @returns 
     */
    const handleReload = () => dispatch(addReloadedQHSEConformityAlerts())

    const handleClickAlert = (id) => {
        dispatch(showProductionStepExecution(id, { from: "alerts" }))
    }
    
    return (
        <>
            {/* -------- header -------- */}
            <Stack direction="row" spacing={2} sx={{ px: 4, py: 2 }} justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Alertes conformité QHSE
                </Typography>
                <Button
                    variant="text"
                    onClick={handleReload}
                    startIcon={<ReplayIcon sx={sx.reloadIcon} color="primary" />}
                    sx={sx.reloadButton}
                >
                    {reloadedProductionStepExecutions.length} changement{reloadedProductionStepExecutions.length > 1 ? "s" : ""}
                </Button>
            </Stack>
            {/* -------- table -------- */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={sx.col}>Heure de l’alerte</TableCell>
                            <TableCell sx={sx.col}>Description</TableCell>
                            <TableCell sx={sx.col}>Recette et section</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {productionStepExecutions.map((row, index) => (
                            <TableRow
                                key={index + row.objectId}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleClickAlert(row.objectId)}
                            >
                                <TableCell sx={sx.col}>
                                    <Typography variant="body1" sx={sx.text}>
                                        {dayjs(row.eventDate).format("HH:mm")}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={sx.text}>
                                    {row.label}
                                    <strong>{row.value}</strong>{row.machine}
                                </TableCell>
                                <TableCell sx={sx.col}>
                                    <Typography variant="body1" sx={sx.recipe}>
                                        {row.uniqueCode} - {row.recipe}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={sx.section}>
                                        {row.section}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <ChevronRightIcon sx={{ width:  "24px", height: "24px" }} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default QHSEConformity